// components/TechStack.jsx — Infinite auto-scroll tech stack ticker
import { useRef } from "react";
import { SectionWrapper, SectionTitle } from "./UI";

const CATEGORY_COLORS = {
  Frontend: { bg: "bg-blue-500/15",   text: "text-blue-400",   border: "border-blue-500/20" },
  Design:   { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/20" },
  Tools:    { bg: "bg-[#A3E635]/15",  text: "text-[#A3E635]",  border: "border-[#A3E635]/20" },
};
const CATEGORY_COLORS_LIGHT = {
  Frontend: { bg: "bg-blue-100",   text: "text-blue-600",   border: "border-blue-200" },
  Design:   { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
  Tools:    { bg: "bg-lime-100",   text: "text-lime-700",   border: "border-lime-200" },
};

function TechCard({ item, dark }) {
  const colors = dark
    ? CATEGORY_COLORS[item.category]  || CATEGORY_COLORS.Tools
    : CATEGORY_COLORS_LIGHT[item.category] || CATEGORY_COLORS_LIGHT.Tools;

  return (
    <div className={`flex-shrink-0 flex items-center gap-3 px-5 py-3.5 rounded-2xl border mx-2 transition-transform duration-300 hover:scale-105 ${dark ? "bg-white/5 border-white/10 hover:border-white/20" : "bg-white border-[#E2E8F0] hover:border-[#A3E635]/40 shadow-sm"}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${dark ? "bg-white/10" : "bg-[#F8FAFC]"}`}>
        {item.logo ? (
          <img src={item.logo} alt={item.name} className="w-5 h-5 object-contain" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <span className="text-sm font-bold text-[#A3E635]">{item.name.charAt(0)}</span>
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <span className={`text-sm font-semibold whitespace-nowrap ${dark ? "text-white" : "text-[#0F172A]"}`}>{item.name}</span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border w-fit mt-0.5 whitespace-nowrap ${colors.bg} ${colors.text} ${colors.border}`}>
          {item.category}
        </span>
      </div>
    </div>
  );
}

export default function TechStack({ content, dark, lang }) {
  const trackRef = useRef(null);
  if (!content?.enabled) return null;
  const items = content.items || [];
  if (items.length === 0) return null;

  const isEs    = lang === "es";
  const title    = isEs ? (content.sectionTitleEs    || content.sectionTitle)    : content.sectionTitle;
  const subtitle = isEs ? (content.sectionSubtitleEs || content.sectionSubtitle) : content.sectionSubtitle;

  const doubled  = [...items, ...items, ...items];
  const duration = `${content.scrollDuration || 40}s`;

  return (
    <SectionWrapper id="tech-stack" dark={dark} bg="alt">
      <SectionTitle title={title || "Tech Stack"} subtitle={subtitle} dark={dark} />

      <ul className="sr-only" aria-label="Technologies used">
        {items.map((item) => <li key={item.id}>{item.name} — {item.category}</li>)}
      </ul>

      <div className="relative overflow-hidden">
        <div className={`absolute inset-y-0 left-0 w-20 z-10 pointer-events-none ${dark ? "bg-gradient-to-r from-[#0F172A] to-transparent" : "bg-gradient-to-r from-[#F1F5F9] to-transparent"}`} />
        <div className={`absolute inset-y-0 right-0 w-20 z-10 pointer-events-none ${dark ? "bg-gradient-to-l from-[#0F172A] to-transparent" : "bg-gradient-to-l from-[#F1F5F9] to-transparent"}`} />
        <div
          ref={trackRef}
          className="flex items-center py-2"
          style={{ animation: `techscroll ${duration} linear infinite`, width: "max-content" }}
          onMouseEnter={(e) => (e.currentTarget.style.animationPlayState = "paused")}
          onMouseLeave={(e) => (e.currentTarget.style.animationPlayState = "running")}
        >
          {doubled.map((item, i) => <TechCard key={`${item.id}-${i}`} item={item} dark={dark} />)}
        </div>
      </div>

      <style>{`@keyframes techscroll { 0% { transform: translateX(0); } 100% { transform: translateX(-33.333%); } }`}</style>
    </SectionWrapper>
  );
}
